import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

/**
 * Hook to observe and track the height of an element using ResizeObserver.
 * Automatically updates when the element's size changes.
 *
 * @param elementRef - Ref to the element to observe
 * @param dependencies - Optional array of dependencies to re-observe when they change
 * @returns The current height of the element (0 if element is not available)
 *
 * @example
 * const headerRef = useRef<HTMLDivElement>(null);
 * const headerHeight = useElementHeight(headerRef, [isDrawerOpen]);
 */
export function useElementHeight<T extends HTMLElement | null>(elementRef: RefObject<T>, dependencies: unknown[] = []): number {
    const [height, setHeight] = useState(0);

    useEffect(() => {
        const updateHeight = () => {
            if (elementRef.current) setHeight(elementRef.current.clientHeight);
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(() => updateHeight());

        if (elementRef.current) resizeObserver.observe(elementRef.current);

        return () => {
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elementRef, ...dependencies]);

    return height;
}
